import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVeriffStatus } from '@/hooks/shared/useVeriffStatus';
import { useGetMe } from '@/hooks/useGetMe';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { TwoFactorAuthModal } from '@/modals/TwoFactorAuthModal';
import { useGetBannerMessages } from '@/query/queries/dashboard/useGetBannerMessages';
import { useGetAmlNotifications } from '@/query/queries/compliance/useGetAmlNotifications';
import { useDismissAmlNotification } from '@/query/queries/compliance/useDismissAmlNotification';
import { logoutUser } from '@/store/services/userServices';
import { ReCaptchaProvider } from '@/providers/recaptcha';
import { AML_CASE_STATUSES } from '@/helpers/compliance';
import { APP_LINKS } from '@/helpers/links';
import { Slider } from './slider';
import { Card } from './slider/card';

export const DashboardMessages = () => {
  const { t } = useTranslation();
  const { openSingleModal } = useModal();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { data: banners } = useGetBannerMessages();
  const { data: amlNotifications = [] } = useGetAmlNotifications();
  const { mutateAsync: dismissAmlCase  } = useDismissAmlNotification();
  const { profile, isSuspended, isPhoneVerified } = useGetMe();
  const slides = [];

  const {
    ready,
    isVeriffSubmitted,
    isVeriffPassed,
    isVeriffApplyError,
    isVeriffApplyRequired,
    isDuplicationAccount,
    isDuplicationPassport,
  } = useVeriffStatus();

  const openVeriff = async () => {
    openSingleModal(MODAL_IDS.country_verification);
  };

  useEffect(() => {
    if (hash === '#veriff' && isVeriffApplyRequired) {
      openVeriff();
    }
  }, [hash, isVeriffApplyRequired]);

  if (isDuplicationPassport) {
    slides.push(
      <Card
        type="error"
        title={t('dashboard:info_message.account_suspended_title')}
        desc={t('dashboard:info_message.duplication_passport_desc')}
        mainLabel={t('dashboard:info_message.log_out')}
        mainAction={logoutUser}
      />,
    );
  }

  if (isDuplicationAccount) {
    slides.push(
      <Card
        type="error"
        title={t('dashboard:info_message.account_suspended_title')}
        desc={t('dashboard:info_message.duplication_account_desc')}
        mainLabel={t('dashboard:info_message.log_out')}
        mainAction={logoutUser}
      />,
    );
  }

  if (isSuspended) {
    slides.push(
      <Card
        type="error"
        title={t('dashboard:info_message.account_suspended_title')}
        desc={t('dashboard:info_message.account_suspended_desc')}
        mainLabel={t('dashboard:info_message.log_out')}
        mainAction={logoutUser}
      />,
    );
  }

  // Veriff status
  if (ready && profile && !isSuspended && !isVeriffPassed) {
    if (isVeriffSubmitted) {
      slides.push(
        <Card
          type="warning"
          title={t('dashboard:info_message.veriff_wait_title')}
          desc={t('dashboard:info_message.veriff_wait_desc')}
        />,
      );
    }

    if (isVeriffApplyError) {
      slides.push(
        <Card
          type="error"
          title={t('dashboard:info_message.veriff_failed_title')}
          desc={t('dashboard:info_message.veriff_failed_desc')}
          mainLabel={t('dashboard:info_message.verify_now')}
          mainAction={openVeriff}
        />,
      );
    }

    if (isVeriffApplyRequired) {
      slides.push(
        <Card
          type="warning"
          title={t('dashboard:info_message.veriff_verify_title')}
          desc={t('dashboard:info_message.veriff_verify_desc')}
          mainLabel={t('dashboard:info_message.verify')}
          mainAction={openVeriff}
        />,
      );
    }
  }

  // 2FA status
  if (profile && !isPhoneVerified) {
    slides.push(
      <>
        <Card
          type="warning"
          title={t('dashboard:info_message.activate_2fa_title')}
          desc={t('dashboard:info_message.activate_2fa_desc')}
          mainLabel={t('dashboard:info_message.turn_on_protection')}
          mainAction={() => openSingleModal(MODAL_IDS.two_factor_auth_modal)}
        />
        <ReCaptchaProvider>
          <TwoFactorAuthModal />
        </ReCaptchaProvider>
      </>,
    );
  }

  amlNotifications.map(item => {
    let amlProps = {
      title: item.title,
      desc: item.description,
    };

    switch (item.status) {
      case AML_CASE_STATUSES.REVIEW:
        amlProps.type = 'warning';
        break;
      case AML_CASE_STATUSES.COMPLETED:
        amlProps.type = 'success';
        amlProps.onClose = () => dismissAmlCase(item.case_id);
        break;
      case AML_CASE_STATUSES.REJECTED:
        amlProps.type = 'error';
        amlProps.onClose = () => dismissAmlCase(item.case_id);
        break;
      case AML_CASE_STATUSES.DOCUMENTS_REQUIRED:
        amlProps.type = 'warning';
        amlProps.mainLabel = t('dashboard:info_message.upload_documents');
        amlProps.mainAction = () => navigate(APP_LINKS.complianceForm(item.case_id));
        break;
      default:
        break;
    }

    amlProps.type && slides.push(<Card {...amlProps} />);
  });

  banners?.map(banner =>
    slides.push(
      <Card
        type={banner.banner_type.toLowerCase()}
        title={banner.title}
        desc={banner.description}
        mainLabel={banner.main_action_label}
        mainAction={
          banner.main_action
            ? () => {
                window.location.href = banner.main_action;
              }
            : undefined
        }
        secondaryLabel={banner.secondary_action_label}
        secondaryAction={
          banner.secondary_action
            ? () => {
                window.location.href = banner.secondary_action;
              }
            : undefined
        }
      />,
    ),
  );

  if (!slides.length) return null;

  return (
    <div>
      {slides.length ? (
        slides.length > 1 ? (
          <Slider slides={slides} />
        ) : (
          <div>{slides[0]}</div>
        )
      ) : null}
    </div>
  );
};
